/**
 * Filter operators enum
 * Matches the backend API operators
 */
export enum SpmFilterOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_OR_EQUAL_TO = 'LESS_THAN_OR_EQUAL_TO',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL_TO = 'GREATER_THAN_OR_EQUAL_TO',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  LIKE = 'LIKE',
  BETWEEN = 'BETWEEN',
  NOT_BETWEEN = 'NOT_BETWEEN',
  IS_NULL = 'IS_NULL',
  JSON_NOT_EXISTS = 'JSON_NOT_EXISTS',
}

/**
 * Filter types available for fields
 */
export type SpmFilterType =
  | 'textInput' // Text input with operator selection
  | 'numberInput' // Number input with operator selection
  | 'singleSelect' // Single choice from a list (dropdown)
  | 'multiSelect' // Multiple choices from a list
  | 'languageSelect' // Language selection (ISO codes)
  | 'countrySelect' // Country selection (ISO codes)
  | 'dateRange'; // Date/datetime picker with range support

/**
 * Operators available for each filter type
 */
export const FILTER_OPERATORS_BY_TYPE: Record<SpmFilterType, SpmFilterOperator[]> = {
  singleSelect: [SpmFilterOperator.EQUALS],

  multiSelect: [
    SpmFilterOperator.IN,
    SpmFilterOperator.NOT_IN,
  ],

  languageSelect: [
    SpmFilterOperator.IN,
    SpmFilterOperator.NOT_IN,
  ],

  countrySelect: [
    SpmFilterOperator.IN,
    SpmFilterOperator.NOT_IN,
  ],

  dateRange: [
    SpmFilterOperator.EQUALS,
    SpmFilterOperator.BETWEEN,
    SpmFilterOperator.NOT_BETWEEN,
    SpmFilterOperator.LESS_THAN,
    SpmFilterOperator.GREATER_THAN,
  ],

  textInput: [
    SpmFilterOperator.CONTAINS,
    SpmFilterOperator.NOT_CONTAINS,
    SpmFilterOperator.STARTS_WITH,
    SpmFilterOperator.ENDS_WITH,
    SpmFilterOperator.EQUALS,
    SpmFilterOperator.NOT_EQUALS,
    SpmFilterOperator.LIKE,
  ],

  numberInput: [
    SpmFilterOperator.EQUALS,
    SpmFilterOperator.NOT_EQUALS,
    SpmFilterOperator.LESS_THAN,
    SpmFilterOperator.LESS_THAN_OR_EQUAL_TO,
    SpmFilterOperator.GREATER_THAN,
    SpmFilterOperator.GREATER_THAN_OR_EQUAL_TO,
    SpmFilterOperator.BETWEEN,
    SpmFilterOperator.NOT_BETWEEN,
  ],
};

/**
 * Default operator for each filter type
 */
export const DEFAULT_OPERATOR_BY_TYPE: Record<SpmFilterType, SpmFilterOperator> = {
  singleSelect: SpmFilterOperator.EQUALS,
  multiSelect: SpmFilterOperator.IN,
  languageSelect: SpmFilterOperator.IN,
  countrySelect: SpmFilterOperator.IN,
  dateRange: SpmFilterOperator.BETWEEN,
  textInput: SpmFilterOperator.CONTAINS,
  numberInput: SpmFilterOperator.EQUALS,
};

/**
 * Operators that require array values (multiple values)
 */
export const ARRAY_VALUE_OPERATORS: SpmFilterOperator[] = [
  SpmFilterOperator.IN,
  SpmFilterOperator.NOT_IN,
  SpmFilterOperator.BETWEEN,
  SpmFilterOperator.NOT_BETWEEN,
];

/**
 * Check if an operator requires array values
 */
export function isArrayOperator(operator: SpmFilterOperator): boolean {
  return ARRAY_VALUE_OPERATORS.includes(operator);
}

/**
 * Check if an operator is a range operator (requires exactly 2 values)
 */
export function isRangeOperator(operator: SpmFilterOperator): boolean {
  return operator === SpmFilterOperator.BETWEEN || operator === SpmFilterOperator.NOT_BETWEEN;
}
